import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  message,
  Popconfirm,
  Row,
  Space,
  Table,
  Modal,
  Tooltip,
  Input,
  Switch,
  notification,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import IMAGES from "../../../const/Image";
import WebAboutRequest from "./webAboutRequest";
import { components, getRowClassName } from "../../../utils/utils";
import { usePermission } from "../../../hooks/usePermission";

export const WebAboutList = () => {
  const hasListPermission = usePermission("Banners", "List");
  const hasAddPermission = usePermission("Banners", "Add");
  const hasEditPermission = usePermission("Banners", "Edit");
  const hasDeletePermission = usePermission("Banners", "Delete");

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [contentType, setContentType] = useState(null);
  const [labList, setLabList] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filter, setFilter] = useState({ page: 1 });
  const [filterForm, setFilterForm] = useState({});

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState("");

  const { confirm } = Modal;

    const showModal = (type, url) => {
    setContentType(type);
    setModalContent(url);
    setIsModalVisible(true);
  };
  
  // ================= FETCH LIST =================
  const fetchServiceList = () => {
    setLoading(true);

    WebAboutRequest.getAboutList(filter)
      .then((response) => {
        const rawData = response?.data?.data?.data || [];

        // 🔥 Transform API data
        const formattedData = rawData.map((item) => {
          const firstSection = item.sections?.[0] || {};

          return {
            key: item.id,
            id: item.id,
            pageTitle: item.pageTitle,
            createdAt: item.createdAt,
            createdBy: item.createdBy,
            updatedBy: item.updatedBy,
            status: item.status,

            title: firstSection.title || item.pageTitle || "-",

            descriptions:
              firstSection.content
                ?.filter((c) => c.type === "paragraph")
                ?.map((c) => c.value)
                ?.join(" ") || "-",

            imageUrl: firstSection.imageUrl || "",
            orderBy: firstSection.orderBy || "-",
          };
        });

        setLabList(formattedData);
        setFilteredBanners(formattedData);
        setTotalRecords(response?.data?.data?.totalItems || 0);
      })
      .catch((error) => {
        message.error("Failed to fetch data");
        console.error(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (hasListPermission) {
      fetchServiceList();
    }
  }, [filter, hasListPermission]);

  // ================= STATUS CHANGE =================
  const handleStatusChange = (checked, item) => {
    confirm({
      title: `Are you sure you want to ${
        checked ? "activate" : "deactivate"
      } this record?`,
      onOk() {
        setLoading(true);

        WebAboutRequest.postUpdateAbout({
          id: item.id,
          status: checked ? 1 : 0,
        })
          .then(() => {
            notification.success({
              message: "Status updated successfully",
            });
            fetchServiceList();
          })
          .catch(() => {
            notification.error({
              message: "Failed to update status",
            });
          })
          .finally(() => setLoading(false));
      },
    });
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    if (!hasDeletePermission) {
      notification.error({
        message: "No permission to delete",
      });
      return;
    }

    setLoading(true);

    WebAboutRequest.postDeleteAbout(id)
      .then(() => {
        message.success("Deleted successfully");
        setLabList((prev) => prev.filter((item) => item.id !== id));
          navigate(0, { state: { refresh: true } });
      })
      .catch(() => message.error("Delete failed"))
      .finally(() => setLoading(false));
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: "S No.",
      render: (_, __, index) => (filter.page - 1) * 10 + index + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Description",
      dataIndex: "descriptions",
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 50 ? text.substring(0, 50) + "..." : text}
        </Tooltip>
      ),
    },
    // {
    //   title: "Image",
    //   dataIndex: "imageUrl",
    //   render: (url) => (
    //     <img
    //       src={url || IMAGES.PlaceholderImage}
    //       alt="about"
    //       style={{
    //         width: "80px",
    //         height: "60px",
    //         objectFit: "cover",
    //         cursor: "pointer",
    //       }}
    //       onClick={() => {
    //         setModalContent(url);
    //         setIsModalVisible(true);
    //       }}
    //     />
    //   ),
    // },
     {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      // width: 120,
      render: (dataIndex) => (
        <img
          src={dataIndex || IMAGES.PlaceholderImage}
          alt="Modal Content"
          style={{ width: "100%", height: "66px" }}
          onClick={() => showModal("image", dataIndex)}
        />
      ),
    },
    {
      title: "Order",
      dataIndex: "orderBy",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <Switch
          checked={status === 1}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleStatusChange(checked, record)}
        />
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space>
          {hasEditPermission && (
            <span
              className="action-icon-bg"
              onClick={() =>
                navigate(`/website-management/about/update/${record.id}`)
              }
            >
              <img src={IMAGES.EditIcon} alt="edit" />
            </span>
          )}

          {hasDeletePermission && (
            <Popconfirm
              title="Delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <span className="action-icon-bg">
                <img src={IMAGES.DeleteIcon} alt="delete" />
              </span>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ================= FILTER =================
  const handleFilter = () => {
    setFilter({ ...filter, ...filterForm, page: 1 });
  };

  const resetFilters = () => {
    setFilter({ page: 1 });
    setFilterForm({});
  };

  // ================= UI =================
  return (
    <>
      <Row gutter={[8, 8]} justify="space-between" align="middle">
          <Col>
            <h1 className="app-heading-med"> About Us List</h1>
          </Col>
          <Col>

            <div style={{ display: "flex", gap: "1rem" }}>
              <Input
                placeholder="Search by banner name"
                size="large"
                className="FilterSearchBox"
                onChange={(e) => setFilterForm({ ...filterForm, search: e.target.value })}
                value={filterForm?.search}
                style={{ width: "100%" }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFilter();
                  }
                }}
              />
              <Button
                onClick={handleFilter}
                type="primary"
                htmlType="submit"
                className="addcity-btn"
                style={{ height: "39px" }}
              >
                <SearchOutlined />
              </Button>
              <Button

                type="primary"
                className="addcity-btn"
                onClick={resetFilters}
                style={{ height: "39px" }}
              >
                Reset
              </Button>
              {hasAddPermission && (
                <Button
                  onClick={() => navigate("/website-management/about/create")}
                  type="primary"
                  className="addcity-btn"
                  style={{ height: "39px" }}
                >
                  <img src={IMAGES.AddIconFilters} alt="Add" />
                </Button>
              )}
            </div>
          </Col>
          {/* <Col style={{ gap: "5px" }}>
            <Button
            onClick={showAddModal}
              type="primary"
              htmlType="submit"
              className="addcity-btn"
            >
              <img src={IMAGES.AddIconFilters} alt="" />
            </Button>
          </Col> */}
        </Row>

      <Table
        style={{ marginTop: 20 }}
        bordered
        loading={loading}
        columns={columns}
        dataSource={filteredBanners}
        rowClassName={getRowClassName}
        components={components}
        pagination={{
          total: totalRecords,
          current: filter.page,
        }}
        onChange={(pagination) =>
          setFilter({ ...filter, page: pagination.current })
        }
      />

      <Modal
        open={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
      >
        <img
          src={modalContent}
          alt="preview"
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
};